import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * Trainer ↔ client direct messaging thread.
 * Polls every 6 seconds for new messages.
 */
export default function MessagesTab({ clientId, user, onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async ({ markRead = true } = {}) => {
    try {
      const { data } = await api.get(`/clients/${clientId}/messages`);
      setMessages(data);
      if (markRead) {
        await api.put(`/clients/${clientId}/messages/read`);
        if (onUnreadChange) onUnreadChange();
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => load({ markRead: false }), 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [clientId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/clients/${clientId}/messages`, { content: text.trim() });
      setText("");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Failed to send");
    }
    setSending(false);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="font-display text-3xl uppercase mb-1">Direct Message</div>
      <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mb-6">
        {user.role === "trainer" ? "Your conversation with this client." : "Reach me anytime. I respond ASAP."}
      </div>

      <div className="border border-zinc-800 bg-[#0A0A0A] h-[55vh] overflow-y-auto p-4 space-y-3" data-testid="messages-thread">
        {messages.length === 0 ? (
          <div className="h-full grid place-items-center text-zinc-600 text-sm">No messages yet. Say hello.</div>
        ) : (
          messages.map(m => {
            const isMine = m.sender_id === user.id;
            return (
              <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                <div className={`max-w-[78%] p-3 border ${isMine ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "bg-[#121212] border-zinc-800 text-zinc-100"}`}>
                  <div className="text-[9px] uppercase tracking-[0.3em] mb-1 opacity-70">
                    {m.sender_role === "trainer" ? "Coach" : m.sender_name || "Client"} · {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="mt-4 flex gap-3">
        <Textarea
          data-testid="message-input"
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }}
          placeholder="Type a message... (Cmd/Ctrl + Enter to send)"
          className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white flex-1"
        />
        <Button data-testid="message-send" type="submit" disabled={sending || !text.trim()}
          className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-white uppercase tracking-[0.2em] h-auto px-6 text-xs self-stretch">
          {sending ? "..." : "Send"}
        </Button>
      </form>
    </div>
  );
}
